import React, { useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell, faBullhorn, faCloudArrowUp, faGraduationCap, faShieldHalved, faTriangleExclamation, faUserGroup, faUsers } from '@fortawesome/pro-solid-svg-icons'
import { useNodeThumbnail } from '../../../../shared/thumbnail-cache/use-node-thumbnail'

// Each notification's leading picture (owner 2026-10-24): a course's own
// thumbnail for a course notification -- the cards' picture, through the
// cards' cache (shared/thumbnail-cache) -- and a category icon in a soft
// circle for everything else: uploads and builds, security, membership,
// groups, product news. Decorative: the row's title says what it is.

const COURSE_ID = /^[1-9]\d{0,17}$/
const LINK = /^edxr:\/\/content\?id=([1-9]\d{0,17})$/

// The course a learning notification is about: the course in its link (C-5
// edxr://content?id=N), else its target when that is a course (a removed
// course has no link). Other categories have no course picture, even when
// they link to one (a finished upload's course): their icon says more.
export const courseIdOf = item => {
  if (!item || item.category !== 'learning') return null
  const m = typeof item.link === 'string' ? LINK.exec(item.link.trim()) : null
  if (m) return m[1]
  const t = item.target
  return t && t.kind === 'course' && COURSE_ID.test(String(t.id)) ? String(t.id) : null
}

// A failure the person may need to act on reads in the danger tone.
const isFailure = item => !!item && typeof item.type === 'string' && /(\.failed|_failed)$/.test(item.type)

const CATEGORY = {
  learning: { icon: faGraduationCap, tone: 'learning' },
  operational: { icon: faCloudArrowUp, tone: 'operational' },
  security: { icon: faShieldHalved, tone: 'security' },
  account: { icon: faUsers, tone: 'account' },
  social: { icon: faUserGroup, tone: 'social' },
  product: { icon: faBullhorn, tone: 'product' }
}

export const categoryVisual = item => {
  if (isFailure(item)) return { icon: faTriangleExclamation, tone: 'failed' }
  return CATEGORY[item && item.category] || { icon: faBell, tone: 'other' }
}

const CategoryIcon = ({ item }) => {
  const v = categoryVisual(item)
  return (
    <span className={`nt-visual nt-visual-icon nt-tone-${v.tone}`} aria-hidden="true" data-testid="inbox-visual-icon">
      <FontAwesomeIcon icon={v.icon} />
    </span>
  )
}

// A 2:1 box (the cards' shape) whatever is in it, so nothing moves when the
// picture arrives: the course placeholder until then, or for good.
const CourseThumbnail = ({ nodeID }) => {
  const ref = useRef(null)
  const url = useNodeThumbnail(nodeID, ref)
  return (
    <span ref={ref} className={`nt-visual nt-visual-course${url ? ' has-picture' : ''}`} aria-hidden="true" data-testid="inbox-visual-course">
      <img src={url || '/content_placeholder.png'} alt="" decoding="async" draggable="false" />
    </span>
  )
}

export const InboxVisual = ({ item }) => {
  const nodeID = courseIdOf(item)
  return nodeID ? <CourseThumbnail nodeID={nodeID} /> : <CategoryIcon item={item} />
}
